import React, { useState, useCallback, useRef } from 'react'
import { sortBy } from 'lodash'
import { useParams } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroller'
import PartnerConversationsApi from '../../api/partners/ConversationsApi'
import EmbedCard from './EmbedCard'
import CardLoadingAnimation from '../shared/CardLoadingAnimation'
import EmbedLayout, { useEmbedParams } from './EmbedLayout'
import { enrichConversations } from './conversationHelpers'
import type { ConversationDisplayData, ConversationApiData, ApiResponse } from './types'

const PAGE_SIZE = 9

const EmbedPartner: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { maxItems } = useEmbedParams()
  const [items, setItems] = useState<ConversationDisplayData[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadingRef = useRef(false)

  const handleLoadMore = useCallback(() => {
    if (loadingRef.current) return
    if (maxItems && items.length >= maxItems) {
      setHasMore(false)
      return
    }
    loadingRef.current = true
    setLoading(true)

    // limit/offset, not page. Api::Partners::ConversationsController passes only
    // limit and offset through to Partners::ConversationService, so a `page`
    // param left both nil -- offset(nil).limit(nil) returns the whole list on
    // every call, hasMore never went false, and InfiniteScroll re-fired forever.
    const offset = items.length

    PartnerConversationsApi.getAll({ slug, limit: PAGE_SIZE, offset })
      .then((res: ApiResponse<ConversationApiData[]>) => {
        // Stops the scroller on a failed request -- see the note in
        // EmbedConversations.tsx. Without it a failing feed retries forever.
        // This is the error path, a separate hole from the limit/offset defect
        // fixed above: on 2026-08-18 the admin embed generator emitted its
        // literal PARTNER_SLUG placeholder, that request 500'd, the bare
        // `return` left hasMore true, and InfiniteScroll re-fired the identical
        // failing request 76 times in ~60 seconds -- each one mailing an
        // exception notification. A 404 is also !ok, so making the API return
        // 404 instead of 500 does not close this on its own.
        if (!res.response?.ok) {
          setHasMore(false)
          return
        }
        const newRaw = res.json || []
        if (newRaw.length < PAGE_SIZE) setHasMore(false)

        const enriched = enrichConversations(newRaw)

        setItems((prev) => {
          const existingIds = new Set(prev.map((c) => c.id))
          const unique = enriched.filter((c) => !existingIds.has(c.id))
          let merged = [...prev, ...unique]
          merged = sortBy(merged, [(o) => o.startsAt])
          if (maxItems) merged = merged.slice(0, maxItems)
          return merged
        })
      })
      .finally(() => {
        loadingRef.current = false
        setLoading(false)
      })
  }, [slug, items.length, maxItems])

  return (
    <EmbedLayout>
      <InfiniteScroll loadMore={handleLoadMore} hasMore={hasMore && !loading} threshold={1000}>
        {items.length === 0 && !loading ? (
          <div className="embed-empty">No conversations available</div>
        ) : (
          <div className="embed-card-grid">
            {items.map((item) => (
              <EmbedCard key={item.id} conversation={item} />
            ))}
          </div>
        )}
        {loading && <CardLoadingAnimation />}
      </InfiniteScroll>
    </EmbedLayout>
  )
}

export default EmbedPartner
